import { useState } from "react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Save, X, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";


export default function PhrasesTab({ phrases, onRefresh }) {
  const [phraseForm, setPhraseForm] = useState({
    text: "",
    phrase_type: "loading",
    active: true
  });
  const [editingPhrase, setEditingPhrase] = useState(null);
  
  // Bulk delete state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  const handlePhraseSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPhrase) {
        await adminApi.put(`/phrases/${editingPhrase.id}`, phraseForm);
        toast.success("Frase actualizada");
      } else {
        await adminApi.post(`/phrases`, phraseForm);
        toast.success("Frase creada");
      }
      resetPhraseForm();
      onRefresh();
    } catch (error) {
      toast.error("Error al guardar frase");
    }
  };

  const handleEditPhrase = (phrase) => {
    setEditingPhrase(phrase);
    setPhraseForm({
      text: phrase.text,
      phrase_type: phrase.phrase_type,
      active: phrase.active
    });
  };

  const handleDeletePhrase = async (phraseId) => {
    if (!window.confirm("¿Eliminar esta frase?")) return;
    try {
      await adminApi.delete(`/phrases/${phraseId}`);
      toast.success("Frase eliminada");
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(phraseId);
        return newSet;
      });
      onRefresh();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error(`Error: ${errorMsg}`);
    }
  };

  // Bulk delete functions
  const toggleSelection = (id) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedItems(new Set(phrases.map(p => p.id)));
  };

  const deselectAll = () => {
    setSelectedItems(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    
    const confirmMessage = `¿Eliminar ${selectedItems.size} frase${selectedItems.size > 1 ? 's' : ''}? Esta acción no se puede deshacer.`;
    if (!window.confirm(confirmMessage)) return;

    setIsDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    const itemIds = Array.from(selectedItems);
    
    for (const itemId of itemIds) {
      try {
        await adminApi.delete(`/phrases/${itemId}`);
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }

    setIsDeleting(false);
    setSelectedItems(new Set());

    if (successCount > 0) {
      toast.success(`${successCount} frase${successCount > 1 ? 's' : ''} eliminada${successCount > 1 ? 's' : ''}`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} frase${errorCount > 1 ? 's' : ''} no se pud${errorCount > 1 ? 'ieron' : 'o'} eliminar`);
    }
    
    onRefresh();
  };

  const resetPhraseForm = () => {
    setEditingPhrase(null);
    setPhraseForm({ text: "", phrase_type: "loading", active: true });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      {/* Phrase Form */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm" data-testid="phrase-form-card">
        <h2 className="text-xl font-bold mb-6 flex items-center gap-2 text-black">
          {editingPhrase ? <Edit2 size={20} /> : <Plus size={20} />}
          {editingPhrase ? "Editar Frase" : "Nueva Frase"}
        </h2>
        <form onSubmit={handlePhraseSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-600 mb-2 font-medium">Texto de la frase *</label>
            <Input
              value={phraseForm.text}
              onChange={(e) => setPhraseForm({...phraseForm, text: e.target.value})}
              placeholder="Cargando... no te desesperes, wey"
              required
              className="bg-gray-50 border-gray-200 text-black focus:border-[#E91E8C]"
              data-testid="phrase-text-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2 font-medium">Tipo</label>
              <Select
                value={phraseForm.phrase_type}
                onValueChange={(value) => setPhraseForm({...phraseForm, phrase_type: value})}
              >
                <SelectTrigger className="bg-gray-50 border-gray-200 text-black" data-testid="phrase-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="loading">Carga</SelectItem>
                  <SelectItem value="empty">Estado vacío</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-400 mt-1">
                Carga: mientras carga el sitio. Vacío: cuando no hay contenido.
              </p>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2 font-medium">Estado</label>
              <Select
                value={phraseForm.active ? "active" : "inactive"}
                onValueChange={(value) => setPhraseForm({...phraseForm, active: value === "active"})}
              >
                <SelectTrigger className="bg-gray-50 border-gray-200 text-black" data-testid="phrase-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="inactive">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="submit" className="bg-black hover:bg-gray-800 text-white rounded-full" data-testid="phrase-submit-btn">
              <Save size={16} className="mr-2" />
              {editingPhrase ? "Actualizar" : "Crear"}
            </Button>
            {editingPhrase && (
              <Button type="button" variant="outline" onClick={resetPhraseForm} className="rounded-full" data-testid="phrase-cancel-btn">
                <X size={16} className="mr-2" />
                Cancelar
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Phrases List */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm" data-testid="phrases-list">
        <h2 className="text-xl font-bold mb-4 text-black">
          Frases Divertidas
          <span className="text-gray-400 font-normal text-base ml-2">({phrases.length})</span>
        </h2>
        
        {/* Bulk Selection Controls */}
        {phrases.length > 0 && (
          <div className="flex items-center justify-between mb-4 p-3 bg-red-50 rounded-xl border border-red-100">
            <div className="flex items-center gap-3">
              <span className="text-sm text-red-700 font-medium">
                {selectedItems.size} seleccionada{selectedItems.size !== 1 ? 's' : ''}
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={selectAll}
                className="text-xs text-red-600 hover:text-red-800"
                disabled={isDeleting}
              >
                Seleccionar todas ({phrases.length})
              </Button>
              {selectedItems.size > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={deselectAll}
                  className="text-xs text-gray-500 hover:text-gray-700"
                  disabled={isDeleting}
                >
                  Deseleccionar
                </Button>
              )}
            </div>
            {selectedItems.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="bg-red-500 hover:bg-red-600"
                data-testid="bulk-delete-phrases-btn"
              >
                {isDeleting ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} className="mr-2" />
                    Eliminar {selectedItems.size}
                  </>
                )}
              </Button>
            )}
          </div>
        )}

        {phrases.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-2">No hay frases personalizadas.</p>
            <p className="text-sm text-gray-400">Se usarán las frases por defecto.</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {phrases.map((phrase) => {
              const isSelected = selectedItems.has(phrase.id);
              return (
                <div 
                  key={phrase.id}
                  className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                    isSelected 
                      ? 'bg-red-50 border-red-300' 
                      : phrase.active 
                        ? 'bg-gray-50 border-gray-100' 
                        : 'bg-gray-100 border-gray-200 opacity-60'
                  }`}
                  data-testid={`phrase-item-${phrase.id}`}
                >
                  {/* Checkbox */}
                  <button
                    type="button"
                    onClick={() => toggleSelection(phrase.id)}
                    disabled={isDeleting}
                    className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 transition-colors mr-3 ${
                      isSelected 
                        ? 'bg-red-500 border-red-500 text-white' 
                        : 'bg-white border-gray-300 hover:border-red-400'
                    }`}
                    data-testid={`select-phrase-${phrase.id}`}
                  >
                    {isSelected && <Check size={12} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-black truncate pr-4">&ldquo;{phrase.text}&rdquo;</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${phrase.phrase_type === 'loading' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                        {phrase.phrase_type === 'loading' ? 'Carga' : 'Vacío'}
                      </span>
                      {!phrase.active && <span className="text-xs text-gray-400">(Inactivo)</span>}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditPhrase(phrase)}
                      className="text-gray-600 hover:text-black"
                      data-testid={`edit-phrase-${phrase.id}`}
                    >
                      <Edit2 size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleDeletePhrase(phrase.id)}
                      data-testid={`delete-phrase-${phrase.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
